import { useEffect, useRef } from 'react';
/** Elements that can hold focus. `:not([disabled])` and hidden inputs excluded. */
const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function tabbable(root) {
    return Array.from(root.querySelectorAll(TABBABLE)).filter(
    // `offsetParent` is null for anything `display:none`; a zero-size element
    // is not reachable either. Neither belongs in the tab cycle.
    (el) => el.offsetParent !== null || el.getClientRects().length > 0);
}
export function useFocusTrap(active, options = {}) {
    const ref = useRef(null);
    // Held in a ref so a caller passing an inline arrow does not tear the trap
    // down and rebuild it — which would re-run the open-focus step every render.
    const opts = useRef(options);
    opts.current = options;
    useEffect(() => {
        if (!active)
            return;
        const node = ref.current;
        if (!node)
            return;
        const restoreTo = document.activeElement;
        const initial = opts.current.initialFocus?.current ?? tabbable(node)[0] ?? node;
        if (initial === node && !node.hasAttribute('tabindex'))
            node.setAttribute('tabindex', '-1');
        initial.focus();
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                opts.current.onEscape?.();
                return;
            }
            if (e.key !== 'Tab' || opts.current.contain === false)
                return;
            const el = ref.current;
            if (!el)
                return;
            const items = tabbable(el);
            if (items.length === 0) {
                // Nothing to cycle through — keep focus on the container rather than
                // letting Tab wander into the page underneath.
                e.preventDefault();
                el.focus();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const current = document.activeElement;
            if (!el.contains(current)) {
                e.preventDefault();
                (e.shiftKey ? last : first).focus();
            }
            else if (e.shiftKey && current === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && current === last) {
                e.preventDefault();
                first.focus();
            }
        }
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            // Only take focus back if the dialog still holds it. If something else
            // has deliberately moved focus on the way out (a navigation, say),
            // yanking it to the old trigger would be the wrong answer.
            const el = ref.current;
            const holdsFocus = !el || el.contains(document.activeElement);
            if (holdsFocus && restoreTo && restoreTo.isConnected)
                restoreTo.focus();
        };
    }, [active]);
    return ref;
}
//# sourceMappingURL=useFocusTrap.js.map